import type { FC } from 'react'
import { useId, useMemo } from 'react'

import { Select as AntSelect, Skeleton } from 'antd'
import classNames from 'classnames'

import stateIcon from 'assets/icons/state-dropdown.svg'

import { Label } from 'common/components/Label/Label'
import Spinner from 'common/components/Spinner/Spinner'
import { ALT_CONSTANTS } from 'common/constants/altConstants'
import { SELECT_CONSTANTS } from 'common/constants/selectConstants'
import type { ISelect } from 'common/interfaces/ISelect'
import { UtilService } from 'common/services/utilService'

import styles from './select.module.scss'
import { Show } from 'common/components/Show/Show'

export const Select: FC<ISelect> = ({
  label,
  isLoading,
  listOptions = [],
  placeholder,
  loading,
  defaultValue,
  proportion = SELECT_CONSTANTS.MEDIUM,
  required,
  error,
  skeletonClassName,
  optionClassName,
  containerClassName,
  options: providedOptions,
  filterOption: customFilterOption,
  ...rest
}) => {
  const id = useId()
  const normalizedListOptions = useMemo(() => {
    return listOptions.map((option: any, index: number) => {
      if (option && typeof option === 'object' && !Array.isArray(option)) {
        const title =
          typeof option.title === 'string'
            ? option.title
            : typeof option.searchText === 'string'
            ? option.searchText
            : typeof option.name === 'string'
            ? option.name
            : typeof option.label === 'string'
            ? option.label
            : ''

        return {
          ...option,
          value: option.value ?? option.code ?? option.id ?? index,
          label: option.label ?? option.name ?? option.value ?? option.code ?? '',
          title,
          className: classNames(option.className, optionClassName, {
            [styles.disabledOption]: option.disabled,
          }),
        }
      }

      const label = String(option)
      return {
        value: option,
        label,
        title: label,
        className: optionClassName,
      }
    })
  }, [listOptions, optionClassName])

  const options = listOptions.length > 0 ? normalizedListOptions : providedOptions

  const defaultFilterOption = (input: string, option?: { title?: unknown; label?: unknown }) => {
    const searchableContent =
      typeof option?.title === 'string'
        ? option.title
        : typeof option?.label === 'string'
        ? option.label
        : typeof option?.label === 'number'
        ? String(option.label)
        : ''

    return searchableContent.toLowerCase().includes(input.toLowerCase())
  }

  return (
    <div className={classNames(styles.container, containerClassName)}>
      <Label id={id} label={label} required={required} />

      {isLoading ? (
        <div
          className={classNames(
            styles.loadingContainer,
            styles[`parent${UtilService.capitalizeFirstLetter(proportion)}`],
            skeletonClassName,
          )}>
          <Skeleton.Input active className={styles.loadingSkeleton} />
        </div>
      ) : (
        <AntSelect
          {...rest}
          id={id}
          key={defaultValue}
          defaultValue={defaultValue}
          placeholder={placeholder || 'None'}
          suffixIcon={
            loading ? (
              <Spinner isLoading />
            ) : (
              rest.suffixIcon || <img src={stateIcon} alt={ALT_CONSTANTS.STATUS_ICON} />
            )
          }
          options={options}
          optionFilterProp='title'
          className={classNames(
            styles.parent,
            rest.className,
            styles[`parent${UtilService.capitalizeFirstLetter(proportion)}`],
            { [styles.parentMultiple]: rest.mode === 'multiple' },
            { [styles.parentDisabled]: rest.disabled },
          )}
          filterOption={customFilterOption || defaultFilterOption}
          virtual={false}
        />
      )}
      <Show when={error}>
        <p className='errorMessage'>{error}</p>
      </Show>
    </div>
  )
}
